import { RootState, INITIAL_STATE, CoverageInfo } from "../core/state-shape";
import { http } from "../lib/http";
import { measure } from "@/lib/performance";

import { simpleHash } from "@/lib/utils";
import { createProjectScopedStore } from './projectScoped';

const loadingPromises = new Map<string, Promise<void>>();
export type FilesState = RootState["files"];

type FilesActions = {
  setState: (newState: FilesState) => void;
  patch: (p: Partial<FilesState>) => void;
  reset: () => void;

  loadFile: (path: string) => Promise<void>;
};

export const useFilesStore = createProjectScopedStore<FilesState & FilesActions>('files', (set, get) => ({
  ...INITIAL_STATE.files,
  setState: (newState) => set(newState),
  patch: (p) => set(p),
  reset: () => set({ ...INITIAL_STATE.files }),

  loadFile: measure(async (path: string) => {
    if (loadingPromises.has(path)) {
      return loadingPromises.get(path);
    }

    const promise = (async () => {
      try {
        const res = await http<{ path: string; content: string; encoding: string; size: number; coverage?: CoverageInfo, coverageDesynced?: boolean }>(
          `/api/files/content?path=${encodeURIComponent(path)}`
        );
        const newHash = simpleHash(res.content);
        const existing = get().cachedContents?.[path];

        // As a further optimization, don't update state if content is identical
        if (existing?.hash === newHash &&
            JSON.stringify(existing.coverage) === JSON.stringify(res.coverage) &&
            (existing.coverageDesynced ?? false) === (res.coverageDesynced ?? false)) {
          return;
        }

        const cachedContents = { ...(get().cachedContents || {}) };
        cachedContents[path] = {
          ...cachedContents[path],
          content: res.content,
          cachedAt: new Date().toISOString(),
          hash: newHash,
          coverage: res.coverage,
          coverageDesynced: res.coverageDesynced ?? false,
        };
        set({ cachedContents } as Partial<FilesState>);
      } finally {
        loadingPromises.delete(path);
      }
    })();
    loadingPromises.set(path, promise);
    return promise;
   }, 'files.loadFile'),
}));